import { useEffect, useMemo } from 'react';
import { useTranscricao } from '../context/TranscricaoContext';
import { createPhraseAudio } from '../utils/phraseAudio';
import checkPronunciation from '../utils/pronunciationChecker';
import useWSTurnState, { TURN_PHASES } from './useWSTurnState';

/**
 * Hook para gerenciar a lógica de completar frases
 * @param {Object} slideData - Dados do slide vindos do WebSocket
 * @param {boolean} shouldProcess - Se deve processar pronúncia (guard de turno)
 * @returns {Object} Objeto com dados da frase e status das palavras
 */
export const usePhraseCompletion = (slideData, shouldProcess = true) => {
  const { transcriptionHistory, addTranscription, resetTranscriptions } = useTranscricao();
  const { currentPhase } = useWSTurnState();

  // Deriva array de words automaticamente de block
  const phraseBlocks = useMemo(() => {
    const rawBlocks = Array.isArray(slideData?.content) ? slideData.content : [];

    return rawBlocks.map(item => {
      const wordStrings = (item.block || '').trim().split(/\s+/);
      return {
        ...item,
        phraseId: slideData?.phraseId, // Passa phraseId para identificar placeholder
        words: wordStrings
          .filter(Boolean)
          .map(word => ({
            word,
            status: 'pending',
            confidence: 0,
            attempts: 0,
            transcribedWord: null,
            block: { ...item, phraseId: slideData?.phraseId } // Inclui phraseId no block
          }))
      };
    });
  }, [slideData?.content, slideData?.phraseId]);

  // Extrai palavras (strings) dos blocos para análise de pronúncia
  const words = useMemo(() => {
    const allWords = [];
    phraseBlocks.forEach(b => {
      b.words.forEach(w => allWords.push(w.word));
    });
    return allWords;
  }, [phraseBlocks]);

  // Monta string completa para display (caso necessário)
  const displayPhrase = useMemo(() => phraseBlocks.map(b => b.block).join(' '), [phraseBlocks]);

  // Verifica pronúncia das palavras SOMENTE se shouldProcess for true
  // Retorna blocos com words atualizados com status
  const blocksWithStatus = useMemo(() => {
    if (!shouldProcess) {
      // Mantém estado "pending" para todas as palavras
      return phraseBlocks;
    }

    const statuses = checkPronunciation(words, transcriptionHistory, addTranscription);

    // Atualiza cada word dentro de cada bloco com seu status
    let wordIndex = 0;
    return phraseBlocks.map(block => ({
      ...block,
      words: block.words.map(wordObj => {
        const updatedWord = {
          ...wordObj,
          ...statuses[wordIndex]
        };
        wordIndex += 1;
        return updatedWord;
      })
    }));
  }, [words, transcriptionHistory, shouldProcess, phraseBlocks, addTranscription]);

  // Extrai todos os wordStatuses de todos os blocos (flatten)
  const allWordStatuses = useMemo(() => {
    const statuses = [];
    blocksWithStatus.forEach(block => {
      statuses.push(...block.words);
    });
    return statuses;
  }, [blocksWithStatus]);

  // Cria função de audio para tocar frase completa
  const { playFullPhrase } = createPhraseAudio(displayPhrase);

  // Reseta transcrições quando entra em A_COMPLETING ou B_COMPLETING
  useEffect(() => {
    const isCompletingPhase = currentPhase === TURN_PHASES.ACompleting || currentPhase === TURN_PHASES.BCompleting;
    if (isCompletingPhase) {
      resetTranscriptions();
    }
  }, [currentPhase, resetTranscriptions]);

  return {
    displayPhrase,
    blocksWithStatus, // Blocos com words atualizados
    allWordStatuses, // Array flat de todos os word objects
    words, // Array de strings das palavras
    playFullPhrase // Função para tocar áudio da frase completa
  };
};
